import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import React, { useRef } from 'react';
import { Faq } from '@/sections/shopWisely/Faq';
import { MoreInfo } from '@/sections/shopWisely/MoreInfo';
import { HowBNPLWorks } from '@/sections/shopWisely/HowBNPLWorks';
import { DontUseBNPL } from '@/sections/shopWisely/DontUseBNPL';
import { Prevention } from '@/sections/shopWisely/Prevention';
import { PaymentOfDebt } from '@/sections/shopWisely/PaymentOfDebt';
import { WhySettleDebtsOnTime } from '@/sections/shopWisely/WhySettleDebtsOnTime';
import { Consulting } from '@/sections/shopWisely/Consulting';
import { ShopSafely } from '@/sections/shopWisely/ShopSafely';
import { Contact } from '@/sections/shopWisely/Contact';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page({
  pageBanner,
  menuPromoBanners
}: Props) {
  const {
    t
  } = useTranslation(['shopWisely', 'core']);
  usePageView('shop-wisely');
  const firstSectionRef = useRef<HTMLDivElement>(null);
  const handleScrollToContact = () => {
    if (firstSectionRef.current) {
      firstSectionRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <Layout menuPromoBanners={menuPromoBanners}>
			<SeoHeader title={t('seo.title')} description={t('seo.description')} />
			<div className="relative overflow-x-hidden">
				<Hero videoId={pageBanner?.videoId} title={pageBanner?.title ?? t('banner.title')} text={pageBanner?.text ?? t('banner.text')} buttons={pageBanner ? pageBanner.buttons?.length ? [{
        ...pageBanner.buttons[0],
        to: undefined,
        onClick: handleScrollToContact
      }, pageBanner.buttons[1]].filter(Boolean) : null : [{
        ariaLabel: t('banner.cta'),
        label: t('banner.cta'),
        onClick: handleScrollToContact,
        testId: 'shopWiselyHowBNPLWorks',
        analyticsParam: 'shopWiselyHowBNPLWorks',
        variant: 'primary'
      }]} variant={pageBanner?.variant || 'mockupMobile'} image={{
        url: pageBanner?.image?.url ?? '/images/sections/shopWisely/hero/cs/hero.png',
        alt: pageBanner?.image?.alt ?? t('banner.title')
      }} />

				<div className="bg-blue-50">
					<SectionDelimiter color="bg-blue-100" background="bg-transparent" />
				</div>

				<div className="relative z-30 scroll-mt-20 bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40" ref={firstSectionRef}>
					<div className="bgCircle absolute top-0 left-0 hidden -translate-x-1/4 translate-y-2/3 rotate-90 lg:block" />
					<div className="bgCircle absolute top-1/4 right-0 translate-x-3/4 -translate-y-1/3 rotate-90 lg:-translate-x-16" />
					<div className="bgCircle absolute bottom-0 left-0 -translate-x-2/3 -translate-y-[20rem] rotate-90 lg:-translate-x-1/4 lg:-translate-y-1/4" />
					<div className="relative z-10 flex flex-col gap-20 lg:gap-40">
						<HowBNPLWorks />
						<DontUseBNPL />
						<Prevention />
						<PaymentOfDebt />
					</div>
				</div>

				<SectionDelimiter color="bg-blue-50" background="bg-blue-100" />

				<div className="relative bg-blue-50 pt-10 pb-20 lg:pt-20 lg:pb-40">
					<div className="bgCircle top:0 absolute right-0 translate-x-3/4 rotate-90 lg:top-1/4 lg:h-96 lg:w-96 lg:translate-x-1/3 lg:-translate-y-1/2" />
					<div className="bgCircle absolute top-1/2 right-0 translate-x-3/4 -translate-y-1/2 rotate-90 lg:hidden" />
					<div className="relative z-10 flex flex-col gap-20 lg:gap-40">
						<WhySettleDebtsOnTime />
						<Consulting />
						<ShopSafely />
					</div>
				</div>

				<SectionDelimiter color="bg-blue-100" background="bg-blue-50" />

				<div className="relative bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">
					<div className="relative z-10 flex flex-col gap-20 lg:gap-40">
						<Contact />
					</div>
				</div>

				<SectionDelimiter color="bg-blue-50" background="bg-blue-100" />

				<div className="relative overflow-hidden bg-blue-50 py-10 lg:py-20">
					<div className="relative z-10 flex flex-col gap-20 lg:gap-40">
						<Faq />
						<MoreInfo />
					</div>
					<div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />
				</div>
			</div>
		</Layout>;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('nakupujte-s-rozumem');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('shop-wisely'),
      ...(await getIntlProps(ctx, ['core', 'shopWisely', 'contact']))
    }
  };
}) satisfies GetStaticProps<Props>;